import React from 'react';
import { Document, Page, View, Text, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingBottom: 28,
    paddingHorizontal: 28,
    fontSize: 10,
    fontFamily: 'Helvetica',
    color: '#111827',
  },
  header: {
    marginBottom: 14,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
  },
  subtitle: {
    marginTop: 4,
    fontSize: 10,
    color: '#6B7280',
  },
  table: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 6,
    overflow: 'hidden',
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingVertical: 8,
    paddingHorizontal: 8,
    alignItems: 'center',
  },
  rowHeader: {
    backgroundColor: '#F9FAFB',
    fontWeight: 700,
  },
  cellImg: {
    width: 46,
    marginRight: 8,
  },
  img: {
    width: 42,
    height: 32,
    objectFit: 'cover',
    borderRadius: 4,
  },
  imgPlaceholder: {
    width: 42,
    height: 32,
    borderRadius: 4,
    backgroundColor: '#F3F4F6',
  },
  cellVehicle: {
    flexGrow: 1,
    flexBasis: 240,
    paddingRight: 8,
  },
  vehicleTitle: {
    fontSize: 10,
    fontWeight: 700,
  },
  vehicleMeta: {
    marginTop: 2,
    fontSize: 9,
    color: '#6B7280',
  },
  cellPrice: {
    width: 110,
    textAlign: 'right',
    fontWeight: 700,
  },
  cellStatus: {
    width: 80,
    textAlign: 'right',
    color: '#374151',
  },
});

export interface VehiclesReportPDFProps {
  vehicles: VehiclesReportVehicle[];
  generatedAtLabel: string;
  getImageUrl: (v: VehiclesReportVehicle) => string | null;
}

export interface VehiclesReportVehicle {
  id: number;
  brand: string;
  model: string;
  year: number;
  plate?: string;
  no_plate?: boolean;
  identification?: string;
  /** Quilometragem (texto como no cadastro, ex.: "45.000") */
  km?: string;
  purchase_price?: number;
  sale_price?: number;
  sale_price_promo?: number;
  status?: string;
  is_consignado?: boolean;
  images?: { id: number; url: string }[];
}

const getVehicleIdLabel = (v: VehiclesReportVehicle): string => {
  if (v.identification) return v.identification;
  if (v.no_plate) return 'Sem placa';
  return v.plate || '—';
};

const getVehicleMetaLine = (v: VehiclesReportVehicle): string => {
  const parts: string[] = [`Identificação: ${getVehicleIdLabel(v)}`];
  const km = v.km?.trim();
  if (km) parts.push(`Quilometragem: ${km} km`);
  if (v.is_consignado) parts.push('Consignado');
  return parts.join(' • ');
};

const formatCurrency = (value: unknown) => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const getStatusLabel = (status?: string) => {
  switch (status) {
    case 'available':
      return 'Disponível';
    case 'sold':
      return 'Vendido';
    case 'maintenance':
      return 'Manutenção';
    default:
      return status ?? '-';
  }
};

const getVehicleValueLabel = (v: VehiclesReportVehicle) => {
  // Preferir preço de venda; fallback para promo; depois compra
  const sale = v.sale_price;
  const salePromo = v.sale_price_promo;
  if (typeof sale === 'number' && sale > 0) return formatCurrency(sale);
  if (typeof salePromo === 'number' && salePromo > 0) return formatCurrency(salePromo);
  if (typeof v.purchase_price === 'number' && v.purchase_price > 0) return formatCurrency(v.purchase_price);
  return '-';
};

const VehiclesReportPDF: React.FC<VehiclesReportPDFProps> = ({ vehicles, generatedAtLabel, getImageUrl }) => {
  return (
    <Document title="Lista de veículos">
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>Lista de veículos</Text>
          <Text style={styles.subtitle}>Gerado em: {generatedAtLabel} • Total: {vehicles.length}</Text>
        </View>

        <View style={styles.table}>
          <View style={[styles.row, styles.rowHeader]}>
            <Text style={styles.cellImg}>Foto</Text>
            <Text style={styles.cellVehicle}>Veículo</Text>
            <Text style={styles.cellPrice}>Valor</Text>
            <Text style={styles.cellStatus}>Status</Text>
          </View>

          {vehicles.map((v) => {
            const imageUrl = getImageUrl(v);
            return (
              <View key={v.id} style={styles.row} wrap={false}>
                <View style={styles.cellImg}>
                  {imageUrl ? <Image style={styles.img} src={imageUrl} /> : <View style={styles.imgPlaceholder} />}
                </View>
                <View style={styles.cellVehicle}>
                  <Text style={styles.vehicleTitle}>
                    {v.brand} {v.model} ({v.year})
                  </Text>
                  <Text style={styles.vehicleMeta}>{getVehicleMetaLine(v)}</Text>
                </View>
                <Text style={styles.cellPrice}>{getVehicleValueLabel(v)}</Text>
                <Text style={styles.cellStatus}>{getStatusLabel(v.status)}</Text>
              </View>
            );
          })}
        </View>
      </Page>
    </Document>
  );
};

export default VehiclesReportPDF;

